// src/callSearch.js
//
// New Call → Find: what did he just type?
//
// The Find step has two boxes — a Phone box up top and one "Name or address"
// box under it. This module reads the second box: is it a person's name, a
// property address, or a name followed by an address ("Mary Jones 123 Oak
// St")? The answer decides two things:
//
//   • how the text is searched (an address searches on house number + street
//     name, so "4521 North Maple Street" still finds "4521 N MAPLE ST"), and
//   • where it lands on a brand-new lead — the bug this replaced put an
//     address typed into Find straight into the lead's Name.
//
// When the exact search finds few people, it also decides what counts as a
// CLOSE match — a typo in a street or a name (see "Close matches" below).
//
// Everything here is pure (no React, no database) so it can be unit tested.
// Parsing never invents a value: a part it isn't sure of stays in the street
// line rather than being guessed into City.

// ── Reference lists ───────────────────────────────────────────
const STATES={AL:"alabama",AK:"alaska",AZ:"arizona",AR:"arkansas",CA:"california",CO:"colorado",CT:"connecticut",
  DE:"delaware",DC:"district of columbia",FL:"florida",GA:"georgia",HI:"hawaii",ID:"idaho",IL:"illinois",IN:"indiana",
  IA:"iowa",KS:"kansas",KY:"kentucky",LA:"louisiana",ME:"maine",MD:"maryland",MA:"massachusetts",MI:"michigan",
  MN:"minnesota",MS:"mississippi",MO:"missouri",MT:"montana",NE:"nebraska",NV:"nevada",NH:"new hampshire",
  NJ:"new jersey",NM:"new mexico",NY:"new york",NC:"north carolina",ND:"north dakota",OH:"ohio",OK:"oklahoma",
  OR:"oregon",PA:"pennsylvania",RI:"rhode island",SC:"south carolina",SD:"south dakota",TN:"tennessee",TX:"texas",
  UT:"utah",VT:"vermont",VA:"virginia",WA:"washington",WV:"west virginia",WI:"wisconsin",WY:"wyoming",PR:"puerto rico"};
const STATE_BY_NAME=(function(){
  const m={};
  Object.keys(STATES).forEach(function(ab){m[STATES[ab]]=ab;});
  // Common written-out abbreviations that can't be mistaken for a word or a name.
  Object.assign(m,{ariz:"AZ",calif:"CA",colo:"CO",fla:"FL",tenn:"TN",okla:"OK",penna:"PA"});
  return m;
})();
// Full state names that are also common first names / surnames ("Joe Montana",
// "Denzel Washington", "Mary Virginia"). They only read as a state when
// something else already says "address" (a house number, a zip, a street type).
const AMBIG_STATE_NAMES=new Set(["virginia","georgia","washington","montana","indiana","florida","nevada","tennessee","carolina","dakota","colorado"]);
// Two-letter states that double as name tokens ("Al", "Mo", "Tom Ma", "Kim Oh",
// "Tuan La", "John Smith MD"). Read as a state only when typed in capitals
// inside otherwise mixed-case text, or with a zip or other address evidence.
const AMBIG_ABBR=new Set(["al","mo","ma","pa","oh","md","me","la"]);

const DIRS={n:"N",s:"S",e:"E",w:"W",ne:"NE",nw:"NW",se:"SE",sw:"SW",north:"N",south:"S",east:"E",west:"W",
  northeast:"NE",northwest:"NW",southeast:"SE",southwest:"SW"};

// Street-type words → 2 = says "street" on its own, 1 = also a common name
// ("Lois Lane", "Grant Hill", "Kevin Way") so it only counts with other evidence.
const TYPES=(function(){
  const t={};
  ("st street str ave av avenue aven rd road dr drive drv ln blvd boulevard blv ct crt cir circle circ crcl wy pl ter terrace terr "+
   "trl trail pkwy parkway pky pkway hwy highway hiway rte route fm cr loop run pt sq square cv cove xing crossing pass path row aly alley "+
   "expy expressway fwy freeway plz plaza walk bnd bend hl holw hollow cres crescent tpke turnpike spur rdg grv gln trce").split(" ")
    .forEach(function(w){t[w]=2;});
  "lane way court park place hill glen trace ridge grove pike point".split(" ").forEach(function(w){t[w]=1;});
  return t;
})();
// Abbreviated street types. One of these always ENDS the street, even when the
// next word is also a street word ("4521 Maple Ave Park City", "… St Bend OR").
// A full word followed by a type can still be the street's name ("123 Park Ave").
const TYPE_ABBR=new Set(("st ave av rd dr ln blvd ct cir pl ter trl pkwy pky hwy cv sq pt wy str drv crt aly expy fwy "+
  "plz bnd hl rdg grv gln trce xing").split(" "));
// A route number follows: "Hwy 60", "Route 66", Texas "FM 1960" (Farm to
// Market), "CR 45" (County Road).
const ROUTE_TYPES=new Set(["hwy","highway","hiway","rte","route","fm","cr"]);
const UNIT_WORDS=new Set(["apt","apartment","unit","ste","suite","spc","space","lot","bldg","building","trlr","trailer","fl","floor","rm","room","dept"]);
const UNIT_GLUED=/^(apt|unit|ste|spc|lot|bldg|rm|fl|trlr)#?\d+[a-z]?$/;
const TITLES=new Set(["mr","mrs","ms","miss","mx","dr","rev","prof","sister","fr"]);
const SUFFIXES=new Set(["jr","sr","ii","iii","iv","md","dds","phd","esq","cpa","dvm","ret"]);
const COUPLE=new Set(["&","and","+"]);
const LETTER="a-z\\u00C0-\\u024F";
const NAME_TOKEN=new RegExp("^(?:["+LETTER+"][\\-'’."+LETTER+"&]*|&|\\+)$","i");
const HAS_LETTER=new RegExp("["+LETTER+"]","i");
const CITY_TOKEN=new RegExp("^["+LETTER+"][\\-'’."+LETTER+"]*$","i");
// What a multi-word city name looks like, for "John Smith Mesa AZ" (where do
// the name's words stop and the city's start?) when the zip table hasn't said.
// Only words that are rarely a person's first name / surname in that spot.
const CITY_FIRST=new Set(("san santa los las la el del de des st ste saint fort ft port mount mt new north south east west "+
  "lake palm salt sun show cave paradise fountain queen big grand little casa sierra rio oro green apache coeur rancho "+
  "laguna mission cape boca punta winter coral the twin post great spanish american cedar eagle sugar round college "+
  "citrus walnut redwood seal yorba buena rolling hacienda culver beverly agoura westlake half arroyo pismo red grass "+
  "elk indian cathedral desert morro paso moreno yucca canyon coos klamath federal gig panama myrtle daytona pompano "+
  "deerfield delray bonita lakewood peachtree saratoga steamboat glenwood highlands johns").split(" "));
const CITY_LAST=new Set(("city valley beach springs spgs junction hills heights hts falls lakes creek village harbor harbour "+
  "point center centre island vista grande verde gardens ranch canyon station forge pass fork mountain mtn oaks bluffs "+
  "prairie landing shores estates acres plains township inlet cove viejo").split(" "));
const CITY_ABBR={st:"saint",ste:"sainte",mt:"mount",ft:"fort",pt:"point"};
// Street-name words written two ways — "500 S ST ANDREWS PL" / "Saint
// Andrews", "MT VIEW" / "Mount View", "QUEEN CRK" / "Queen Creek" — read as
// one word (the long form). "St" only counts as Saint before more of the
// name; as the last word it is Street.
const NAME_CANON={st:"saint",saint:"saint",mt:"mount",mount:"mount",ft:"fort",fort:"fort",
  crk:"creek",creek:"creek",mtn:"mountain",mountain:"mountain"};
// "Third Ave" is "3RD AVE" on the mail lists.
const ORDINAL_WORDS={first:1,second:2,third:3,fourth:4,fifth:5,sixth:6,seventh:7,eighth:8,ninth:9,tenth:10};
function ordinalSuffix(n){return n===1?"st":n===2?"nd":n===3?"rd":"th";}

// ── Small helpers ─────────────────────────────────────────────
function collapse(s){return String(s==null?"":s).replace(/\s+/g," ").trim();}
// A trailing country — what a Google Maps copy ends in: "…, AZ 85201, USA",
// "… United States". Left on, it hides the zip and the state and the whole
// line lands in Street (and finds nobody). Only dropped with something before
// it; a bare "US" only after a comma or in an address with digits, never as
// the last word of a name.
const COUNTRY_TAIL=/([\s,]+)(?:(u\.?\s?s\.?\s?a\.?|united\s+states(?:\s+of\s+america)?)|(u\.?\s?s\.?))\s*$/i;
function stripCountry(s){
  const m=COUNTRY_TAIL.exec(s);
  if(!m||!m.index)return s;
  const rest=s.slice(0,m.index);
  if(m[3]&&m[1].indexOf(",")<0&&!/\d/.test(rest))return s;
  return collapse(rest).replace(/[,\s]+$/,"");
}
function key(w){return String(w||"").toLowerCase().replace(/[.,;:]+/g,"");}
function emptyAddress(){return{street:"",city:"",state:"",zip:"",full:""};}
// Split on spaces; a comma becomes a flag on the token before it, never a token.
function tokenize(s){
  const out=[];
  collapse(s).replace(/,/g," , ").split(" ").forEach(function(t){
    if(!t)return;
    if(t===","){if(out.length)out[out.length-1].comma=true;return;}
    out.push({w:t,comma:false});
  });
  return out;
}
function joinW(toks){return collapse(toks.map(function(t){return t.w;}).join(" "));}
// A name keeps the commas it was typed with ("Smith, John"), minus a trailing one.
function joinName(toks){
  return collapse(toks.map(function(t,i){return t.w+(t.comma&&i<toks.length-1?",":"");}).join(" ")).replace(/[,\s]+$/,"");
}
function isType(w){return !!TYPES[key(w)];}
function isHouse(w){
  const k=key(w);
  return /^\d+[a-z]?$/.test(k)||/^\d+-[a-z0-9]+$/.test(k)||/^[nsew]\d+(?:[nsew]\d+)?$/.test(k);
}
function isFraction(w){return /^\d\/\d+$/.test(key(w));}
function isUnitAt(toks,i,end){
  if(i>=end)return false;
  const k=key(toks[i].w);
  if(/^#./.test(k)||UNIT_GLUED.test(k))return true;
  return (k==="#"||UNIT_WORDS.has(k))&&i+1<end;
}
// Step past "Apt 5", "#5", "# 5", "Unit B", "Bldg 3 Apt 12"…
function consumeUnits(toks,k,end){
  while(k<end){
    const w=key(toks[k].w);
    if(/^#./.test(w)||UNIT_GLUED.test(w)){k++;continue;}
    if((w==="#"||UNIT_WORDS.has(w))&&k+1<end){k+=2;continue;}
    break;
  }
  return k;
}
function compose(a){
  const line=[a.street,[a.city,a.state].filter(Boolean).join(", ")].filter(Boolean).join(", ");
  return collapse(line+(a.zip?" "+a.zip:""));
}

// ── Known cities ──
// One spelling-proof key per city: "St. George", "Saint George" and "st george"
// all → "saintgeorge"; "McLean" and "Mc Lean" → "mclean".
function cityKey(words){
  return words.map(function(w){
    const k=key(typeof w==="string"?w:w.w).replace(/['’.-]/g,"");
    return CITY_ABBR[k]||k;
  }).join("");
}
// cityKeySet(["Mesa", "St George", …]) → the Set classifyQuery's `cities`
// option takes. Built once from the app's own zip table (public/zips.json —
// the cities the leads actually live in).
export function cityKeySet(names){
  const s=new Set();
  (names||[]).forEach(function(n){const k=cityKey(collapse(n).split(" ").filter(Boolean));if(k)s.add(k);});
  return s;
}
// Shaped like a city name: starts with a city word ("San Tan Valley", "Lake
// Havasu City"), or is two words ending in one ("Apache Junction", "Park
// City"). A longer run ending in "City" is not ("Smith Park City").
function cityShaped(toks){
  const n=toks.length;
  if(!n)return false;
  if(CITY_FIRST.has(key(toks[0].w)))return true;
  return n<=2&&CITY_LAST.has(key(toks[n-1].w));
}

// What an address can start with: a house number ("4521", "4521-B", "12 1/2",
// Wisconsin's "N1234"), a PO Box, or a rural route ("HC 2 Box 15").
function startConstruct(toks,i,end){
  if(i>=end)return null;
  const k0=key(toks[i].w),k1=i+1<end?key(toks[i+1].w):"",k2=i+2<end?key(toks[i+2].w):"";
  let n=0;
  if(k0==="po"&&k1==="box")n=2;
  else if(k0==="p"&&k1==="o"&&k2==="box")n=3;
  else if(k0==="pob"||k0==="pobox")n=1;
  else if(k0==="post"&&k1==="office"&&k2==="box")n=3;
  else if(k0==="box"&&/^\d/.test(k1))n=1;
  if(n){
    if(i+n<end&&/^[a-z]?\d+[a-z0-9-]*$/.test(key(toks[i+n].w)))n++;
    return{type:"po",len:n};
  }
  let r=0;
  if((k0==="hc"||k0==="hcr"||k0==="rr")&&/^\d+$/.test(k1))r=2;
  else if(/^(hc|hcr|rr)\d+$/.test(k0))r=1;
  else if((k0==="rural"||k0==="star")&&k1==="route")r=/^\d+$/.test(k2)?3:2;
  if(r){
    if(i+r+1<end&&key(toks[i+r].w)==="box"&&/^\d/.test(key(toks[i+r+1].w)))r+=2;
    return{type:"rural",len:r};
  }
  if(isHouse(toks[i].w)){
    return{type:"house",len:i+1<end&&isFraction(toks[i+1].w)?2:1};
  }
  return null;
}

// The state at the END of the tokens, if it is one. `minIdx` keeps a street's
// own name from being read as a state ("123 Washington", "4521 N Utah").
function findState(toks,end,minIdx,o){
  if(end<1)return null;
  const floor=Math.max(minIdx,0);
  for(const n of [3,2,1]){
    const s=end-n;
    if(s<floor)continue;
    const part=toks.slice(s,end);
    if(n>1&&part.slice(0,-1).some(function(t){return t.comma;}))continue;
    const phrase=part.map(function(t){return key(t.w);}).join(" ");
    const ab=STATE_BY_NAME[phrase];
    if(!ab)continue;
    if(n===1&&AMBIG_STATE_NAMES.has(phrase)&&!(o.zip||o.ctx||o.lenient))continue;
    return{abbr:ab,start:s};
  }
  const s=end-1;
  if(s<floor)return null;
  const raw=toks[s].w.replace(/[.,]+$/,"");
  const k=key(raw);
  if(k.length!==2||!STATES[k.toUpperCase()])return null;
  const prev=s>0?toks[s-1]:null;
  const afterComma=!!(prev&&prev.comma);
  // "Main St NE" — a post-directional, not Nebraska. "1234 E 1200 NE" — a grid
  // coordinate.
  if(DIRS[k]&&prev&&!afterComma&&(isType(prev.w)||/^\d+$/.test(key(prev.w))))return null;
  // "123 Oak Ct" / "123 Maple Wy" — the street's own suffix, not Connecticut or
  // Wyoming: a house number, then no street type until this word.
  // ("123 Main St Hartford CT" and "Hartford CT 06103" are still the state.)
  if(TYPES[k]&&o.street&&prev&&!afterComma&&!toks.slice(floor,s).some(function(t){return isType(t.w);}))return null;
  const caps=/^[A-Z]{2}$/.test(raw);
  if(AMBIG_ABBR.has(k)&&!((caps&&o.mixedCase)||o.zip||o.ctx||o.lenient))return null;
  // "Ann Ga", "Sue Ok", "Bob In": with nothing else saying "address", a
  // title-case two-letter word in Mixed Case text is a surname. A state is
  // typed in capitals ("Mesa AZ") or after a comma ("Mesa, Az").
  if(!(o.zip||o.ctx||o.lenient)&&o.mixedCase&&o.upper&&!caps&&!afterComma)return null;
  return{abbr:k.toUpperCase(),start:s};
}

// ── The address parser ────────────────────────────────────────
// Works right to left for the parts that sit at the end (zip, then state),
// then left to right for the street (number, name, type, unit). City is what
// is left between them — and only when that is clear.
function parseInfo(toks,o){
  const info={addr:emptyAddress(),sc:null,typeAt:-1,typeKey:"",unit:false,state:false,zip:false,stateStart:-1};
  let end=toks.length;
  if(!end)return info;
  const a=info.addr;
  // ZIP — the last token, unless it is a PO Box / unit / route number. "FL"
  // before five digits is Florida, not a floor number ("Naples FL 34102").
  const lastW=toks[end-1].w.replace(/[.,]+$/,"");
  if(/^\d{5}(?:-\d{4})?$/.test(lastW)){
    const pk=end>=2?key(toks[end-2].w):"";
    const numbered=pk==="box"||pk==="#"||(UNIT_WORDS.has(pk)&&!STATES[pk.toUpperCase()])||ROUTE_TYPES.has(pk);
    if(end===1||!numbered){a.zip=lastW;info.zip=true;end--;}
  }
  const sc=startConstruct(toks,0,end);
  info.sc=sc;
  // Address context: anything that already says "this is an address".
  let ctx=!!sc;
  for(let i=1;i<end&&!ctx;i++){if(TYPES[key(toks[i].w)]===2||isUnitAt(toks,i,end))ctx=true;}
  let minIdx;
  if(sc&&sc.type==="house"){
    minIdx=sc.len+1;
    if(sc.len<end&&DIRS[key(toks[sc.len].w)])minIdx++;
  }else if(sc){minIdx=sc.len;}
  else minIdx=a.zip?0:1;
  const house=!!(sc&&sc.type==="house");
  const st=findState(toks,end,minIdx,{zip:info.zip,ctx:ctx,street:house,mixedCase:o.mixedCase,upper:o.upper,lenient:o.lenient});
  if(st){a.state=st.abbr;info.state=true;end=st.start;}
  info.stateStart=end;

  // Street
  let streetEnd,cityOK=false;
  // "St" + a word that isn't a street type is Saint ("St Andrews", "St Charles").
  const saintAt=function(i){return i+1<end&&key(toks[i].w)==="st"&&!toks[i].comma&&!isType(toks[i+1].w);};
  if(sc&&(sc.type==="po"||sc.type==="rural")){
    streetEnd=consumeUnits(toks,Math.min(sc.len,end),end);
    cityOK=true;
  }else{
    let nameStart=sc?Math.min(sc.len,end):0;
    // A leading directional is part of the street ("N Maple", "W St Andrews
    // Pl") unless it IS the street's name ("123 N St").
    if(nameStart+1<end&&DIRS[key(toks[nameStart].w)]&&
      (!isType(toks[nameStart+1].w)||(nameStart+2<end&&isType(toks[nameStart+2].w))||saintAt(nameStart+1)))nameStart++;
    let typeAt=-1,commaAt=-1,unitAt=-1,grid=false;
    // A Utah-style grid street: "1234 E 1200 S" — a number, then a directional,
    // and no street type. A spelled-out directional only counts at the end of
    // the street; mid-line it may be the city's ("1234 W 1200 South Jordan"),
    // so the whole line stays in Street rather than guessing.
    if(house&&nameStart+1<end&&/^\d+$/.test(key(toks[nameStart].w))&&!toks[nameStart].comma&&DIRS[key(toks[nameStart+1].w)]){
      const dk=key(toks[nameStart+1].w);
      const atEnd=nameStart+2>=end||toks[nameStart+1].comma||isUnitAt(toks,nameStart+2,end);
      grid=true;
      if(dk.length<=2||atEnd){
        streetEnd=consumeUnits(toks,nameStart+2,end);
        if(streetEnd>nameStart+2)info.unit=true;
        cityOK=true;
      }else streetEnd=end;
    }
    // "12345 Hwy 60" — the type comes first and a route number follows.
    if(!grid&&nameStart+1<end&&ROUTE_TYPES.has(key(toks[nameStart].w))&&/^\d+[a-z]?$/.test(key(toks[nameStart+1].w)))typeAt=nameStart;
    // A full street word followed by another type word is part of the name
    // ("Park" in "123 Park Ave") — but an abbreviation always ends the street
    // ("4521 Maple Ave Park City", "123 Main St St George UT"), and so does
    // a word before "St" + a name, which is Saint ("123 Park St George").
    const typeNext=function(j){
      if(j+1>=end||toks[j].comma||!isType(toks[j+1].w))return false;
      if(TYPE_ABBR.has(key(toks[j].w)))return false;
      return !saintAt(j+1);
    };
    for(let j=nameStart;!grid&&j<end&&typeAt<0;j++){
      if(j>nameStart&&isType(toks[j].w)&&!typeNext(j)){typeAt=j;break;}
      if(j>nameStart&&isUnitAt(toks,j,end)){unitAt=j;break;}
      if(toks[j].comma){commaAt=j;break;}
    }
    if(grid){
      // streetEnd set above
    }else if(typeAt>=0){
      info.typeAt=typeAt;info.typeKey=key(toks[typeAt].w);
      let k=typeAt+1;
      const tk=info.typeKey;
      const route=ROUTE_TYPES.has(tk)||((tk==="rd"||tk==="road")&&typeAt>0&&["county","co","state","forest"].indexOf(key(toks[typeAt-1].w))>=0);
      if(route&&k<end&&!toks[typeAt].comma&&/^(\d+[a-z]?|[a-z])$/.test(key(toks[k].w)))k++;
      // Post-directional: "Main St NE". A spelled-out one only at the end of
      // the street, so "North Las Vegas" stays the city.
      if(k<end&&!toks[k-1].comma){
        const dk=key(toks[k].w);
        if(DIRS[dk]&&(dk.length<=2||k+1>=end||toks[k].comma||isUnitAt(toks,k+1,end)))k++;
      }
      const before=k;
      k=consumeUnits(toks,k,end);
      if(k>before)info.unit=true;
      streetEnd=k;cityOK=true;
    }else if(unitAt>=0){
      streetEnd=consumeUnits(toks,unitAt,end);info.unit=true;cityOK=true;
    }else if(sc&&commaAt>=0){
      const before=commaAt+1;
      streetEnd=consumeUnits(toks,before,end);
      if(streetEnd>before)info.unit=true;
      cityOK=true;
    }else if(!sc&&(info.state||info.zip)){
      streetEnd=0;                       // "Mesa AZ 85201" — no street at all
    }else{
      streetEnd=end;                     // can't tell where the street stops: keep it all
    }
  }
  let street=toks.slice(0,streetEnd);
  const rest=toks.slice(streetEnd,end);
  if(rest.length){
    const letters=rest.every(function(t){return CITY_TOKEN.test(t.w.replace(/[.,]+$/,""));});
    const anchored=info.state||info.zip||cityOK;
    if(letters&&anchored&&rest.length<=4)a.city=joinW(rest);
    else street=toks.slice(0,end);
  }
  a.street=joinW(street).replace(/[,\s]+$/,"");
  a.full=compose(a);
  return info;
}

// Case flags for the text as typed ("AZ" and "Az" read differently).
function caseFlags(s,extra){
  return Object.assign({mixedCase:/[a-z]/.test(s),upper:/[A-Z]/.test(s)},extra||{});
}
// parseAddress(text) → {street, city, state, zip, full}. The text is taken to
// BE an address (the chip was flipped to Address), so a state is read even
// where a name would be likelier ("Denver Colorado", "Mesa Az").
export function parseAddress(text){
  const s=stripCountry(collapse(text));
  return parseInfo(tokenize(s),caseFlags(s,{lenient:true})).addr;
}

// ── Name or address? ──────────────────────────────────────────
function nameResult(toks,confidence){
  return{kind:"name",name:joinName(toks),address:emptyAddress(),confidence:confidence};
}
function mixedResult(nameToks,addr,confidence){
  return{kind:"mixed",name:joinName(nameToks),address:addr,confidence:confidence};
}
// Did the parse find a word that can only be a street type? Not a weak one
// ("Park", "Lane") and not "St" + a word, which is Saint ("Mary St Clair",
// "St George").
function typeSaysStreet(info,toks){
  if(info.typeAt<0)return false;
  if(TYPES[info.typeKey]!==2)return false;
  if(info.typeKey==="st"&&info.typeAt<toks.length-1&&!toks[info.typeAt].comma)return false;
  return true;
}
// Does this letters-only text say "address" on its own?
function strongAddress(info,toks){
  if(info.state||info.zip||info.sc||info.unit)return true;
  return typeSaysStreet(info,toks);
}
// "John Smith Mesa AZ": only words before a state — no street, no zip, no
// comma. Where do the name's words stop and the city's start? Returns
// {name, city} token lists, or null when it's all city ("San Tan Valley AZ").
// Trusted in this order: the zip table's own cities (o.cities), then what a
// city name looks like ("Lake …", "… Valley"), then word count — three or
// more words end in the city ("John Smith Mesa"); two words with no city in
// sight are a name and a state ("John Smith AZ"). With the zip table loaded,
// a last word it doesn't know is never guessed into City.
function allCityWords(T){return T.length>0&&T.every(function(t){return CITY_TOKEN.test(t.w.replace(/[.]+$/,""));});}
function splitNameCity(W,o){
  const m=W.length;
  if(m<2)return null;
  const known=!!(o.cities&&o.cities.size);
  if(known){
    for(let len=Math.min(4,m);len>=1;len--){
      const tail=W.slice(m-len);
      if(!allCityWords(tail)||!o.cities.has(cityKey(tail)))continue;
      if(len===m)return null;
      const lead=W.slice(0,m-len);
      return nameLike(lead,o)?{name:lead,city:tail}:null;
    }
  }
  if(m<=4&&allCityWords(W)&&cityShaped(W))return null;
  for(let len=Math.min(3,m-1);len>=2;len--){
    const tail=W.slice(m-len);
    if(!allCityWords(tail)||!cityShaped(tail))continue;
    const lead=W.slice(0,m-len);
    if(nameLike(lead,o))return{name:lead,city:tail};
  }
  if(m===2||known)return nameLike(W,o)?{name:W,city:[]}:null;
  const lead=W.slice(0,m-1),tail=W.slice(m-1);
  return allCityWords(tail)&&nameLike(lead,o)?{name:lead,city:tail}:null;
}
function classifyLetters(toks,o){
  const n=toks.length;
  if(!n)return{kind:"unknown",name:"",address:emptyAddress(),confidence:0};
  const keys=toks.map(function(t){return key(t.w);});
  const title=n>1&&TITLES.has(keys[0]);
  const suffix=n>1&&SUFFIXES.has(keys[n-1]);
  const couple=n>2&&keys.some(function(k,i){return i>0&&i<n-1&&COUPLE.has(k);});
  // "John Smith Jr", "John Smith MD" — a suffix ends a name, never a state.
  if(suffix)return nameResult(toks,0.9);
  // "John Smith, Mesa AZ" / "Mr. John Smith, Mesa AZ" — a name, then where
  // they live.
  const c=toks.findIndex(function(t){return t.comma;});
  if(c>=0&&c<n-1){
    const pre=toks.slice(0,c+1),post=toks.slice(c+1);
    const pi=parseInfo(post,o);
    if(strongAddress(pi,post)&&nameLike(pre,o))return mixedResult(pre,pi.addr,0.7);
  }
  const info=parseInfo(toks,o);
  // "John Smith Mesa AZ", "John & Mary Smith Gilbert AZ" — name words, then
  // a state, and nothing else.
  if(info.state&&!info.zip&&!info.sc&&!info.unit&&!typeSaysStreet(info,toks)&&info.stateStart>=2){
    const W=toks.slice(0,info.stateStart);
    if(W.every(function(t){return !t.comma&&NAME_TOKEN.test(t.w);})){
      const sp=splitNameCity(W,o);
      if(sp){
        const a=emptyAddress();
        a.city=joinW(sp.city);a.state=info.addr.state;a.full=compose(a);
        return mixedResult(sp.name,a,0.6);
      }
    }
  }
  if(title||couple)return nameResult(toks,0.9);
  if(strongAddress(info,toks))return{kind:"address",name:"",address:info.addr,confidence:info.state||info.typeAt>=0?0.75:0.65};
  // Weak hints — an ambiguous street word, a leading directional — leave it a
  // name, at low confidence. Search still matches streets either way.
  if(info.typeAt>=0||(n>1&&DIRS[keys[0]]))return nameResult(toks,0.45);
  return nameResult(toks,n===1?0.3:n<=4?0.85:0.6);
}
// Could these leading words be a person's name (the "Mary Jones" in "Mary
// Jones 123 Oak St")? Never a unit, box, route, street word or state.
// `relaxed` — a real street follows (house number + street name), so the
// words before it can only be a name: "St" + a name is Saint ("Mary St
// Clair 123 Oak St"), a surname may be a full street word ("Maria Plaza",
// "Bob Street"), and a title-case "Ga" is a surname ("Ann Ga 9 Elm St").
function nameLike(toks,o,relaxed){
  const n=toks.length;
  if(!n)return false;
  const ok=toks.every(function(t,i){
    const raw=t.w.replace(/[,]+$/,"");
    const k=key(raw);
    if(!NAME_TOKEN.test(raw))return false;
    if(UNIT_WORDS.has(k)||k==="#"||k==="box"||k==="po"||k==="pob"||k==="hc"||k==="hcr"||k==="rr")return false;
    if(TYPES[k]===2&&!TITLES.has(k)){
      if(!relaxed)return false;
      if(k==="st")return i<n-1&&!t.comma&&!isType(toks[i+1].w);
      return !TYPE_ABBR.has(k)&&!ROUTE_TYPES.has(k);
    }
    if(k.length<=2&&DIRS[k]&&n===1)return false;
    return true;
  });
  if(!ok)return false;
  // "AZ 85201", "Arizona 85201" — a state before the zip, not a person.
  const lastRaw=toks[n-1].w.replace(/[.,]+$/,""),last=key(lastRaw);
  if(last.length===2&&STATES[last.toUpperCase()]&&!AMBIG_ABBR.has(last)&&
    !(relaxed&&o.mixedCase&&o.upper&&!/^[A-Z]{2}$/.test(lastRaw)))return false;
  if(STATE_BY_NAME[last]&&!AMBIG_STATE_NAMES.has(last))return false;
  if(relaxed)return true;
  return classifyLetters(toks,o).kind==="name";
}

// classifyQuery(text, opts) → {kind, name, address:{street,city,state,zip,full}, confidence}
//   kind: "address" | "name" | "mixed" (a name, then an address) | "unknown"
//   opts.cities — optional cityKeySet(...) of known city names; it only
//   decides where a name stops and a city starts in "John Smith Mesa AZ".
export function classifyQuery(text,opts){
  const raw=stripCountry(collapse(text));
  const empty={kind:"unknown",name:"",address:emptyAddress(),confidence:0};
  if(!raw)return empty;
  const hasLetters=HAS_LETTER.test(raw),hasDigits=/\d/.test(raw);
  const o=caseFlags(raw,{cities:(opts&&opts.cities)||null});
  if(!hasLetters){
    if(!hasDigits)return empty;
    const t=raw.replace(/\s+/g,"");
    if(/^\d{5}(?:-\d{4})?$/.test(t)){const a=emptyAddress();a.zip=t;a.full=t;return{kind:"address",name:"",address:a,confidence:0.8};}
    // Seven or more digits is a phone number (or a fragment of one) — the
    // Phone box's job, not a name or an address.
    if(raw.replace(/\D/g,"").length>=7)return empty;
    // A house number on its way to being an address.
    if(/^\d+(?:-\d+)?(?:\s+\d\/\d+)?$/.test(raw)){const a=emptyAddress();a.street=raw;a.full=raw;return{kind:"address",name:"",address:a,confidence:0.4};}
    return empty;
  }
  const toks=tokenize(raw);
  if(!hasDigits)return classifyLetters(toks,o);
  // Digits never appear in a name. Name words first, then an address, is
  // "mixed" — split at the first house number / PO Box / rural route.
  let k=-1;
  for(let i=0;i<toks.length;i++){if(startConstruct(toks,i,toks.length)){k=i;break;}}
  if(k>0){
    const pre=toks.slice(0,k),ri=parseInfo(toks.slice(k),o);
    // A real street after it — a number AND a street name, or a PO Box /
    // rural route — not a bare number ("Oak Street 123") or a zip.
    const relaxed=!!(ri.sc&&ri.addr.street.split(" ").length>=2);
    if(nameLike(pre,o,relaxed))return mixedResult(pre,ri.addr,0.8);
  }
  const info=parseInfo(toks,o);
  return{kind:"address",name:"",address:info.addr,confidence:k===0||info.zip?0.9:0.6};
}

// The chip under the box flips what the text is read as. Returns the
// classification for `kind` ("name" | "address" | "mixed"), or the automatic
// one when kind is empty or already what was detected.
export function classifyAs(text,kind,opts){
  const auto=classifyQuery(text,opts);
  if(!kind||kind===auto.kind)return auto;
  const raw=collapse(text);
  if(!raw)return auto;
  if(kind==="name")return{kind:"name",name:raw.replace(/[,\s]+$/,""),address:emptyAddress(),confidence:1,forced:true};
  if(kind==="address")return{kind:"address",name:"",address:parseAddress(raw),confidence:1,forced:true};
  return auto;
}
// What a click on the chip cycles through, starting at the detected kind.
export function kindOptions(auto){
  if(!auto||auto.kind==="unknown")return[];
  if(auto.kind==="mixed")return["mixed","address","name"];
  return auto.kind==="address"?["address","name"]:["name","address"];
}
export function nextKind(auto,current){
  const opts=kindOptions(auto);
  if(!opts.length)return null;
  const i=opts.indexOf(current);
  return opts[(i+1)%opts.length];
}
function cityLine(a){
  return collapse([a.city,a.state].filter(Boolean).join(", ")+(a.zip?" "+a.zip:""));
}
// "Address · 4521 N Maple St · Mesa, AZ 85201" / "Name" / "Name + address · …"
export function kindLabel(cls){
  if(!cls||cls.kind==="unknown")return"";
  const a=cls.address||emptyAddress();
  const where=[a.street,cityLine(a)].filter(Boolean).join(" · ");
  if(cls.kind==="name")return"Name";
  if(cls.kind==="address")return"Address"+(where?" · "+where:"");
  return"Name + address · "+[cls.name,where].filter(Boolean).join(" · ");
}

// ── Where things go on a brand-new lead ──
// Name → name; address parts → street/city/state/zip. `address` (the composed
// one-line address) is only set with a street, so a lead created from "Mesa
// AZ" never gets "Mesa, AZ" written into its street column.
export function placeForNewLead(cls){
  const a=(cls&&cls.address)||emptyAddress();
  const named=cls&&(cls.kind==="name"||cls.kind==="mixed");
  return{
    name:named?cls.name:"",
    street:a.street||"",city:a.city||"",state:a.state||"",zip:a.zip||"",
    address:a.street?(a.full||compose(a)):"",
  };
}
// The new-lead button's wording, so he can see where it will put things.
export function newLeadSummary(cls){
  if(!cls||cls.kind==="unknown")return"";
  const a=cls.address||emptyAddress();
  const where=a.full||compose(a);
  if(cls.kind==="name")return cls.name;
  if(cls.kind==="address")return where;
  return [cls.name,where].filter(Boolean).join(" at ");
}

// ── Search ────────────────────────────────────────────────────
// Strip what the database search strips (searchLeadsRemote: ( ) , * % . ")
// so "4521 N. Maple, Mesa" and "4521 N Maple Mesa" search the same.
export function normalizeSearchText(s){
  return String(s==null?"":s).replace(/[(),*%."]/g," ").replace(/\s+/g," ").trim();
}
function nameTerms(name){
  const ks=normalizeSearchText(name).toLowerCase().split(" ").filter(Boolean);
  const kept=ks.filter(function(k){return !COUPLE.has(k)&&!TITLES.has(k)&&!SUFFIXES.has(k);});
  return kept.length?kept:ks;
}
// House number + street name: what every spelling of one address shares.
// "N" vs "North", "St" vs "Street", "#5" vs "Apt 5" are left out.
export function streetTerms(street){
  const t=normalizeSearchText(street).toLowerCase().split(" ").filter(Boolean);
  if(!t.length)return[];
  const bi=t.indexOf("box");
  if(bi>=0&&bi<t.length-1){
    return t.slice(0,bi).filter(function(w){return /\d/.test(w)||w==="hc"||w==="hcr"||w==="rr";}).concat(["box",t[bi+1]]);
  }
  const out=[];let i=0;
  if(/^\d/.test(t[0])||/^[nsew]\d/.test(t[0])){out.push(t[0]);i=1;if(t[1]&&/^\d\/\d+$/.test(t[1])){out.push(t[1]);i=2;}}
  const words=[];
  for(;i<t.length;i++){
    const w=t[i];
    if((w==="#"||UNIT_WORDS.has(w))&&i+1<t.length){i++;continue;}
    if(/^#/.test(w)||UNIT_GLUED.test(w))continue;
    words.push(w);
  }
  if(words.length>1&&DIRS[words[0]])words.shift();      // pre-directional
  if(words.length>1&&ROUTE_TYPES.has(words[0]))words.shift();  // "Hwy 60" / "Highway 60" — the number is the name
  let core=words.filter(function(w,j){return j===0||(!DIRS[w]&&!TYPES[w]);});
  if(!core.length&&words.length)core=[words[0]];
  // "Third" is written "3RD" on the mail lists. A name word written two ways
  // ("Saint"/"St", "Mount"/"Mt", "Creek"/"Crk") can't be one search word for
  // both, so it's left out while another street-name word remains:
  // "500 Saint Andrews Pl" searches 500 + andrews, and finds ST ANDREWS too.
  core=core.map(function(w){const n=ORDINAL_WORDS[w];return n?n+ordinalSuffix(n):w;});
  const plain=core.filter(function(w){return !NAME_CANON[w];});
  if(plain.length)core=plain;
  const terms=out.concat(core);
  return terms.length?terms:t;
}
function cityTerms(a){
  return normalizeSearchText([a.city,a.state,String(a.zip||"").slice(0,5)].filter(Boolean).join(" ")).toLowerCase().split(" ").filter(Boolean);
}
function patToken(w){return String(w||"").toLowerCase().replace(/[^a-z0-9/-]+/g,"*").replace(/\*+/g,"*").replace(/^\*|\*$/g,"");}
// The database's address column holds the street line only, so the pattern
// is the house number (+ "1/2") and the first street-name word:
// "4521*maple*" finds "4521 N MAPLE ST" and "4521 North Maple Street" alike.
function addressPattern(st){
  const toks=st.map(patToken).filter(Boolean);
  if(!toks.length)return null;
  if(/^\d/.test(toks[0])||/^[nsew]\d/.test(toks[0])){
    let n=1;
    if(toks[1]&&/^\d\/\d+$/.test(toks[1]))n=2;
    if(toks.length<=n)return null;                     // a bare number is too broad
    return toks.slice(0,n+1).join("*")+"*";            // anchored on the house number
  }
  if(toks[0]==="box"||toks[0]==="hc"||toks[0]==="hcr"||toks[0]==="rr")return "*"+toks.join("*")+"*";
  return toks[0].length>=4?"*"+toks[0]+"*":null;
}
// searchTermsFor(cls) → {local, locals, remote, patterns}
//   local    — the query for the in-memory index (searchProspectIndex)
//   locals   — local first, then fallbacks: a name + address that isn't on
//              file together still finds the address alone, then the name
//   remote   — the text for the database search (searchLeadsRemote), "" = skip
//   patterns — extra database ilike patterns ({col, pat}, "*" = wildcard) that
//              tolerate N/North, St/Street and name order ("Smith John")
export function searchTermsFor(cls){
  const none={local:"",locals:[],remote:"",patterns:[]};
  if(!cls||cls.kind==="unknown")return none;
  const a=cls.address||emptyAddress();
  const pats=[];
  const nameT=cls.kind==="name"||cls.kind==="mixed"?nameTerms(cls.name):[];
  const st=cls.kind==="address"||cls.kind==="mixed"?streetTerms(a.street):[];
  if(nameT.length>=2){
    const nt=nameT.map(patToken).filter(Boolean);
    if(nt.length>=2){
      pats.push({col:"name",pat:"*"+nt.join("*")+"*"});
      pats.push({col:"name",pat:"*"+[nt[nt.length-1]].concat(nt.slice(0,-1)).join("*")+"*"});
    }
  }
  const ap=addressPattern(st);
  if(ap)pats.push({col:"address",pat:ap});
  const where=(st.length?st:cityTerms(a)).join(" ");
  let locals,remote;
  if(cls.kind==="name"){
    locals=[nameT.join(" ")];remote=normalizeSearchText(cls.name);
  }else if(cls.kind==="address"){
    locals=[where];
    remote=a.street?normalizeSearchText(a.street):"";
  }else{
    // Name + city/state with no street ("John Smith Mesa AZ"): never fall back
    // to the city alone — that lists everyone in Mesa.
    locals=[nameT.concat(where?where.split(" "):[]).join(" ")].concat(st.length?[where]:[],[nameT.join(" ")]);
    remote=a.street?normalizeSearchText(a.street):normalizeSearchText(cls.name);
  }
  locals=locals.filter(function(q,i){return q&&q.length>=2&&locals.indexOf(q)===i;});
  return{local:locals[0]||"",locals:locals,remote:remote,patterns:pats};
}
// Keep only rows whose haystack really holds every term. searchProspectIndex
// also matches an all-digit query against phone numbers; in the Name/address
// box "4521" is a house number, never the middle of someone's phone.
export function keepTextMatches(rows,local){
  const terms=String(local||"").toLowerCase().split(/\s+/).filter(Boolean);
  if(!terms.length)return[];
  return (rows||[]).filter(function(r){
    const h=String((r&&r.hay)||"");
    return terms.every(function(t){return h.indexOf(t)>=0;});
  });
}
// Best match first: a name search ranks rows whose NAME holds every word; an
// address search ranks rows whose ADDRESS holds the street, then city / zip.
// Ties keep the index's own order (files before leads).
export function rankResults(rows,cls){
  if(!cls||!rows||rows.length<2)return rows||[];
  const a=cls.address||emptyAddress();
  const nt=cls.kind==="name"||cls.kind==="mixed"?nameTerms(cls.name):[];
  const st=cls.kind==="address"||cls.kind==="mixed"?streetTerms(a.street):[];
  const city=key(a.city),zip=String(a.zip||"").slice(0,5);
  const all=function(hay,terms){return terms.length>0&&terms.every(function(t){return hay.indexOf(t)>=0;});};
  return rows.map(function(r,i){
    const nm=String(r.name||"").toLowerCase(),sub=String(r.sub||"").toLowerCase(),hay=String(r.hay||"");
    let s=0;
    if(all(nm,nt))s+=4;
    if(all(sub,st))s+=4;
    if(city&&hay.indexOf(city)>=0)s+=1;
    if(zip&&hay.indexOf(zip)>=0)s+=1;
    return{r:r,s:s,i:i};
  }).sort(function(x,y){return y.s-x.s||x.i-y.i;}).map(function(x){return x.r;});
}
// Rows found by BOTH boxes first (the phone confirms the name/address), then
// what the Name/address box found, then phone-only rows. Once he has typed a
// name or address, the phone's own hit is the "not them" he just ruled out —
// Enter must open who he typed, not the number's owner. With nothing typed
// below, there are no text rows and the phone hits lead.
// `closeHits` (findCloseMatches) always come after every exact row — a close
// match the phone confirms first among them — and before phone-only rows.
// Each row comes back as a copy carrying `via`: "both" | "text" | "phone".
export function combineResults(phoneHits,textHits,limit,closeHits){
  const id=rowKey;
  const uniq=function(arr){const seen=new Set();return (arr||[]).filter(function(r){const k=id(r);if(seen.has(k))return false;seen.add(k);return true;});};
  const ph=uniq(phoneHits),tx=uniq(textHits);
  const inText=new Set(tx.map(id)),inPhone=new Set(ph.map(id));
  const cl=uniq(closeHits).filter(function(r){return !inText.has(id(r));});
  const inClose=new Set(cl.map(id));
  const tag=function(v){return function(r){return Object.assign({},r,{via:v});};};
  return ph.filter(function(r){return inText.has(id(r));}).map(tag("both"))
    .concat(tx.filter(function(r){return !inPhone.has(id(r));}).map(tag("text")))
    .concat(cl.filter(function(r){return inPhone.has(id(r));}).map(tag("both")))
    .concat(cl.filter(function(r){return !inPhone.has(id(r));}).map(tag("text")))
    .concat(ph.filter(function(r){return !inText.has(id(r))&&!inClose.has(id(r));}).map(tag("phone")))
    .slice(0,limit||50);
}
function rowKey(r){return r.kind+"-"+r.id;}

// ── Close matches ─────────────────────────────────────────────
// Reece (10/1): "have the address search not require a perfect match". When
// what was typed finds fewer than CLOSE_MATCH_BELOW people exactly, Find also
// lists CLOSE matches — labelled "Close match", always under the exact ones:
//   • an address: the SAME house number, and each street-name word spelled
//     within a typo ("4521 Mapel" → 4521 N MAPLE ST). Street type, direction,
//     unit, city, state and zip never decide a match, they only rank it — so
//     "4521 Maple Rd" still finds 4521 Maple Ave (as a close match, because
//     the street type differs). A different house number is a different house
//     and never matches — nor is 12 1/2 the same house as 12, nor 1234 E
//     1200 S the same as 1234 E 1200 N (a grid street's last direction is
//     part of its name). "Saint"/"St", "Mount"/"Mt", "Creek"/"Crk" and
//     "Third"/"3rd" are the same word.
//   • an address typed without a house number: the street name in the typed
//     zip or city ("Mapel St Mesa AZ"). A street name alone is too broad.
//   • a name: every name word within a typo ("Jon Smyth" → John Smith), in
//     any order ("Smith John"); &/and, Mr/Mrs/Dr and Jr/Sr/III are ignored.
// "Within a typo" is a Damerau-Levenshtein distance (a swapped pair of
// letters is one edit) of 1, or 2 for a word of 7+ letters. Numbers and
// one- or two-letter words must match exactly (a two-letter name word may
// be an initial: "J Smith").
export const CLOSE_MATCH_BELOW=3;
const SUFFIX_CANON=(function(){
  const m={};
  [["st","street","str"],["ave","av","avenue","aven","avn"],["rd","road"],["dr","drive","drv"],["ln","lane"],
   ["blvd","boulevard","blv"],["ct","court","crt"],["cir","circle","circ","crcl"],["way","wy"],["pl","place"],
   ["ter","terrace","terr"],["trl","trail"],["pkwy","parkway","pky","pkway"],["hwy","highway","hiway"],["rte","route"],
   ["pt","point"],["sq","square"],["cv","cove"],["xing","crossing"],["aly","alley"],["expy","expressway"],
   ["fwy","freeway"],["plz","plaza"],["bnd","bend"],["hl","hill"],["holw","hollow"],["cres","crescent"],
   ["tpke","turnpike"],["rdg","ridge"],["grv","grove"],["gln","glen"],["trce","trace"]]
    .forEach(function(g){g.forEach(function(w){m[w]=g[0];});});
  return m;
})();
function canonType(w){return SUFFIX_CANON[w]||w;}

// editDistance("mapel","maple") → 1. Optimal-string-alignment Damerau-
// Levenshtein: insert, delete, substitute, or swap two neighbours, 1 each.
// With `max`, gives up early and returns max+1 once it's further than that.
export function editDistance(a,b,max){
  a=String(a==null?"":a);b=String(b==null?"":b);
  const lim=max==null?Infinity:max;
  const la=a.length,lb=b.length;
  if(Math.abs(la-lb)>lim)return lim+1;
  if(!la||!lb)return Math.max(la,lb);
  let p2=new Array(lb+1),p1=new Array(lb+1),c=new Array(lb+1);
  for(let j=0;j<=lb;j++)p1[j]=j;
  for(let i=1;i<=la;i++){
    c[0]=i;
    let rowMin=i;
    const ca=a.charCodeAt(i-1);
    for(let j=1;j<=lb;j++){
      const cb=b.charCodeAt(j-1);
      let v=Math.min(p1[j]+1,c[j-1]+1,p1[j-1]+(ca===cb?0:1));
      if(i>1&&j>1&&ca===b.charCodeAt(j-2)&&a.charCodeAt(i-2)===cb&&p2[j-2]+1<v)v=p2[j-2]+1;
      c[j]=v;
      if(v<rowMin)rowMin=v;
    }
    // A swap jumps two rows, but never below the row before's minimum + 1,
    // so once a whole row is past the limit nothing later comes back under.
    if(rowMin>lim)return lim+1;
    const t=p2;p2=p1;p1=c;c=t;
  }
  return p1[lb]>lim?lim+1:p1[lb];
}
// How far a typed word may be from the stored one.
function wordTol(w){return /\d/.test(w)||w.length<=2?0:w.length>=7?2:1;}
// Lowercase words; apostrophes dropped ("O'Brien" → obrien), anything else
// that isn't a letter or a digit splits ("Mary-Kate" → mary, kate).
function plainWords(s){
  return String(s==null?"":s).toLowerCase().replace(/['’]/g,"")
    .split(/[^a-z0-9À-ɏ]+/).filter(Boolean);
}
function ordinalBare(w){return /^\d+(?:st|nd|rd|th)$/.test(w)?w.replace(/\D+$/,""):w;}
// The parts of a street line: house number, the street's own name words,
// and the street type / direction (which rank a match but never decide it).
// "4521 N Maple St Apt 5" → {house:"4521", words:["maple"], suffix:"st", dir:"N"}
// Only the LAST word can be the street type; an earlier type word is part of
// the name ("Pine Hill Rd" → words pine, hill; suffix rd). `types` lists every
// type word in the line, so "4521 Pine Hill" never reads as disagreeing with
// "4521 PINE HILL RD".
// Name words written two ways are read one way: "St Andrews" / "Saint
// Andrews" → saint, "Mt" → mount, "Crk" → creek, "Third" / "3rd" → 3.
// `frac` is a "1/2" after the house number (12 1/2 is not 12). `pre` / `post`
// are the directions before and after the name; `dir` is whichever there is
// (the leading one first). A Utah-style grid street ("1234 E 1200 S": a number
// for a name, no street type) is `grid`, and its trailing direction is part
// of the street — 1200 S and 1200 N are different streets.
function streetParts(street){
  const t=normalizeSearchText(street).toLowerCase().replace(/['’]/g,"").split(" ").filter(Boolean);
  const out={house:"",frac:"",words:[],suffix:"",dir:"",pre:"",post:"",grid:false,types:[],box:false};
  if(!t.length)return out;
  if(t.indexOf("box")>=0){out.box=true;return out;}            // PO Box / rural route: exact only
  let i=0;
  if(/^\d/.test(t[0])||/^[nsew]\d/.test(t[0])){out.house=t[0];i=1;if(t[1]&&/^\d\/\d+$/.test(t[1])){out.frac=t[1];i=2;}}
  const words=[];
  for(;i<t.length;i++){
    const w=t[i];
    if((w==="#"||UNIT_WORDS.has(w))&&i+1<t.length){i++;continue;}
    if(/^#/.test(w)||UNIT_GLUED.test(w))continue;
    words.push(w);
  }
  if(words.length>1&&DIRS[words[0]])out.dir=out.pre=DIRS[words.shift()];
  if(words.length>1&&ROUTE_TYPES.has(words[0])){out.suffix=canonType(words.shift());out.types.push(out.suffix);}
  let last=words.length-1;
  while(last>0&&DIRS[words[last]])last--;                      // "Main St NE": NE is after the type
  words.forEach(function(w,j){
    if(j>last){
      if(!out.post)out.post=DIRS[w];
      if(!out.dir)out.dir=DIRS[w];
      return;
    }
    if(w==="st"&&j<last){out.words.push("saint");return;}       // "St Andrews": Saint, not Street
    if(j>0&&TYPES[w]){
      out.types.push(canonType(w));
      if(j===last){out.suffix=canonType(w);return;}
    }
    out.words.push(nameWord(w));
  });
  out.grid=!out.suffix&&out.words.length>0&&out.words.every(function(w){return /^\d+$/.test(w);});
  return out;
}
function nameWord(w){
  const n=ORDINAL_WORDS[w];
  if(n)return String(n);
  return w==="st"?w:(NAME_CANON[w]||ordinalBare(w));
}
// Do the typed and stored street types disagree? Never when either is
// missing, or when one line holds the other's type anywhere in it.
function typesDisagree(a,b){
  return !!(a.suffix&&b.suffix&&b.types.indexOf(a.suffix)<0&&a.types.indexOf(b.suffix)<0);
}
// The two directions to compare, or "" where one is missing. A grid street
// compares its leading directions ("1234 E …" vs "1234 W …"); its trailing
// ones are part of the street's name (gridSideDiffers). Anywhere else a
// street has one direction, wherever it was written ("N Maple St",
// "Main St NE").
function dirPair(a,b){return a.grid||b.grid?[a.pre,b.pre]:[a.dir,b.dir];}
function dirsDisagree(a,b){const d=dirPair(a,b);return !!(d[0]&&d[1]&&d[0]!==d[1]);}
// "1234 E 1200 S" and "1234 E 1200 N" are different streets.
function gridSideDiffers(a,b){return !!(a.grid&&b.grid&&a.post&&b.post&&a.post!==b.post);}
// "4521", "4521B", "4521-B" are one house; "45210" is another.
function houseKey(h){const m=/^[nsew]?\d+/.exec(String(h||""));return m?m[0]:String(h||"");}
// Is `b` the house typed as `a`? The same number ("4521B" is 4521) and the
// same fraction ("12 1/2" is not "12"). `b` needs a house number of its own.
function sameHouse(a,b){
  return !!(b&&b.house)&&houseKey(b.house)===houseKey(a.house)&&(b.frac||"")===(a.frac||"");
}
// Each typed word against the closest stored word not already used.
// → {dist, inOrder} or null when any typed word has nothing close enough.
function matchWords(typed,cand,initials){
  const used=new Array(cand.length).fill(false);
  let dist=0,inOrder=true,last=-1;
  for(let k=0;k<typed.length;k++){
    const w=typed[k],tol=wordTol(w);
    let best=-1,bd=tol+1;
    for(let j=0;j<cand.length&&bd>0;j++){
      if(used[j])continue;
      const c=cand[j];
      const d=tol===0?(c===w||(initials&&w.length<=2&&!/\d/.test(w)&&c.indexOf(w)===0)?0:1):editDistance(w,c,tol);
      if(d<bd){bd=d;best=j;}
    }
    if(best<0)return null;
    used[best]=true;dist+=bd;
    if(best<last)inOrder=false;
    last=best;
  }
  return{dist:dist,inOrder:inOrder};
}
// The typed street words against a stored street: each one close to a word of
// the street's name — or of its city, for an address typed without commas
// whose street and city couldn't be told apart ("4521 Mapel Mesa AZ") — and
// at least one of them on the street itself. `cityTail`: the typed address
// ends in a state or zip but no city was split off, so the street line may
// END in the city — once a word after the street matches nothing, it and the
// rest are that city, which only ranks ("4521 Mapel Gilbert AZ 85233" still
// finds 4521 MAPLE ST in Mesa, below the one in Gilbert).
function matchStreetWords(typed,core,city,cityTail){
  const used=new Array(core.length).fill(false);
  let dist=0,onStreet=0;
  for(let k=0;k<typed.length;k++){
    const w=typed[k],tol=wordTol(w);
    let best=-1,bd=tol+1;
    for(let j=0;j<core.length&&bd>0;j++){
      if(used[j])continue;
      const d=tol===0?(core[j]===w?0:1):editDistance(w,core[j],tol);
      if(d<bd){bd=d;best=j;}
    }
    if(best>=0){used[best]=true;dist+=bd;onStreet++;continue;}
    let cd=tol+1;
    for(let j=0;j<city.length&&cd>0;j++){
      const d=tol===0?(city[j]===w?0:1):editDistance(w,city[j],tol);
      if(d<cd)cd=d;
    }
    if(cd<=tol){dist+=cd;continue;}
    if(cityTail&&onStreet)break;
    return null;
  }
  return onStreet?{dist:dist}:null;
}
function sameCity(a,b){
  if(!a||!b)return false;
  return a===b||(a.length>=5&&editDistance(a,b,1)<=1);
}
// Where a result row is, read once per row (the index rows are rebuilt only
// when the data changes, so a WeakMap keeps this off every keystroke).
// A lead keeps its street line apart from city/state/zip; a pipeline file has
// one free-text property line.
const placeCache=new WeakMap();
const nameCache=new WeakMap();
function rowPlace(r){
  let p=placeCache.get(r);
  if(p)return p;
  const raw=(r&&r.raw)||{};
  let a;
  if(r.kind==="lead"){
    const line=String(raw.address||"");
    if(line.indexOf(",")>=0){
      const pa=parseAddress(line);
      a={street:pa.street,city:raw.city||pa.city,state:raw.state||pa.state,zip:raw.zip||pa.zip};
    }else a={street:line,city:raw.city||"",state:raw.state||"",zip:raw.zip||""};
  }else a=parseAddress(r.sub||"");
  const city=plainWords(a.city);
  p={st:streetParts(a.street),city:city,cityKey:cityKey(city),state:String(a.state||"").toLowerCase(),
    zip:String(a.zip||"").replace(/\D/g,"").slice(0,5)};
  placeCache.set(r,p);
  return p;
}
function rowNameWords(r){
  let w=nameCache.get(r);
  if(!w){w=r.name==="(unnamed)"?[]:plainWords(r.name);nameCache.set(r,w);}
  return w;
}
// fuzzyTermsFor(cls) → what a close match is measured against, or null when
// there is too little to guess from (a bare house number, a street name with
// no house number, zip or city, a one- or two-letter name).
//   {kind, name: [words] | null, street: {house, words, suffix, dir} | null,
//    city: [words], cityKey, state, zip}
export function fuzzyTermsFor(cls){
  if(!cls||cls.kind==="unknown")return null;
  const a=cls.address||emptyAddress();
  let name=null,street=null;
  if(cls.kind==="name"||cls.kind==="mixed"){
    const w=plainWords(cls.name);
    const kept=w.filter(function(k){return !COUPLE.has(k)&&!TITLES.has(k)&&!SUFFIXES.has(k);});
    name=kept.length?kept:w;
    if(!name.some(function(k){return k.length>=3;}))name=null;
  }
  const city=plainWords(a.city),zip=String(a.zip||"").replace(/\D/g,"").slice(0,5);
  if((cls.kind==="address"||cls.kind==="mixed")&&a.street){
    const st=streetParts(a.street);
    // A one-letter street name is too little to guess from — a number is not
    // ("315 W 3rd Ave" → 3).
    if(!st.box&&st.words.some(function(k){return k.length>=2||/^\d+$/.test(k);})&&(st.house||zip.length===5||city.length))street=st;
  }
  if(!name&&!street)return null;
  return{kind:cls.kind,name:name,street:street,city:city,cityKey:cityKey(city),
    state:String(a.state||"").toLowerCase(),zip:zip.length===5?zip:""};
}
// How close one row is to what was typed: a score (higher = closer), or null
// when it isn't a close match at all.
function placeBonus(ft,p){
  let s=0;
  if(ft.zip&&p.zip===ft.zip)s+=3;
  if(ft.cityKey&&sameCity(ft.cityKey,p.cityKey))s+=2;
  if(ft.state&&p.state===ft.state)s+=1;
  return s;
}
function streetScore(ft,p){
  const t=ft.street;
  if(!t||p.st.box)return null;
  if(t.house){
    if(!sameHouse(t,p.st))return null;
  }else if(!((ft.zip&&p.zip===ft.zip)||(ft.cityKey&&sameCity(ft.cityKey,p.cityKey))))return null;
  if(gridSideDiffers(t,p.st))return null;
  const m=matchStreetWords(t.words,p.st.words,p.city,!ft.city.length&&!!(ft.state||ft.zip));
  if(!m)return null;
  let s=40-10*m.dist;
  if(t.suffix&&p.st.suffix)s+=typesDisagree(t,p.st)?-2:1;
  const d=dirPair(t,p.st);
  if(d[0]&&d[1])s+=d[0]===d[1]?1:-3;
  return s;
}
// closeMatchScore(row, fuzzyTermsFor(cls)) → a score (higher = closer; a name
// AND an address both close score highest), or null when the row isn't a
// close match at all.
export function closeMatchScore(row,ft){
  if(!row||!ft)return null;
  let ns=null;
  if(ft.name){
    const m=matchWords(ft.name,rowNameWords(row),true);
    if(m)ns=40-10*m.dist+(m.inOrder?1:0);
  }
  if(ns==null&&!ft.street)return null;
  // A row whose text doesn't even hold the house number can't be that house
  // (checked before the row's address is read).
  const hk=ft.street&&ft.street.house?houseKey(ft.street.house):"";
  const skipStreet=!ft.street||(hk&&typeof row.hay==="string"&&row.hay.indexOf(hk)<0);
  if(ns==null&&skipStreet)return null;
  const p=rowPlace(row);
  const ss=skipStreet?null:streetScore(ft,p);
  if(ns==null&&ss==null)return null;
  return (ns||0)+(ss||0)+placeBonus(ft,p);
}
// Exact text hits whose street type or direction disagrees with what was
// typed ("4521 Maple Rd" found 4521 MAPLE AVE; "4521 S Maple" found
// 4521 N MAPLE) are close matches, not exact ones. Hits that aren't the
// typed house at all are dropped: the text search matches "4521" anywhere,
// so it also finds 45210 and 14521, zip 84521 and lead MK-14521 — and
// 12 1/2 for 12, or 1234 E 1200 N for 1234 E 1200 S. A name + address whose
// name matched as typed stays exact. → {exact, close}
export function splitExactClose(rows,cls){
  const list=rows||[];
  const a=(cls&&cls.address)||emptyAddress();
  if(!cls||!(cls.kind==="address"||cls.kind==="mixed")||!a.street)return{exact:list,close:[]};
  const t=streetParts(a.street);
  if(t.box)return{exact:list,close:[]};
  const nt=cls.kind==="mixed"?nameTerms(cls.name):[];
  const exact=[],close=[];
  list.forEach(function(r){
    const nm=String(r.name||"").toLowerCase();
    if(nt.length>0&&nt.every(function(k){return nm.indexOf(k)>=0;})){exact.push(r);return;}
    const st=rowPlace(r).st;
    if((t.house&&!sameHouse(t,st))||gridSideDiffers(t,st))return;
    (typesDisagree(t,st)||dirsDisagree(t,st)?close:exact).push(r);
  });
  return{exact:exact,close:close};
}
// findCloseMatches(indexes, cls, {exclude, also, limit}) → close rows, closest
// first, each a copy carrying close:true and closeScore.
//   indexes — arrays of search-index rows (buildProspectIndex) to look through
//   exclude — rows already listed as exact matches (never repeated)
//   also    — rows to list as close (splitExactClose's) even when their
//             spelling is further off than a close match allows ("4521 Map
//             Rd" for 4521 MAPLE AVE) — but only at the typed house
// Cheap checks first: an address with a house number only reads rows whose
// text holds that number, so a 70K-row index stays a fast scan.
export function findCloseMatches(indexes,cls,opts){
  const o=opts||{};
  const ft=fuzzyTermsFor(cls);
  const typedSt=cls&&cls.address&&cls.address.street&&(cls.kind==="address"||cls.kind==="mixed")?streetParts(cls.address.street):null;
  const skip=new Set((o.exclude||[]).map(rowKey));
  const out=[];
  const add=function(r,s){skip.add(rowKey(r));out.push({r:r,s:s,i:out.length});};
  (o.also||[]).forEach(function(r){
    if(!r||skip.has(rowKey(r)))return;
    const s=ft?closeMatchScore(r,ft):null;
    if(s!=null){add(r,s);return;}
    if(typedSt&&!typedSt.box){
      const st=rowPlace(r).st;
      if((typedSt.house&&!sameHouse(typedSt,st))||gridSideDiffers(typedSt,st))return;   // another house
    }
    add(r,20);
  });
  if(ft){
    const hk=!ft.name&&ft.street&&ft.street.house?houseKey(ft.street.house):"";
    const zipOnly=!ft.name&&ft.street&&!ft.street.house&&ft.zip?ft.zip:"";
    const cityWord=!ft.name&&ft.street&&!ft.street.house&&!ft.zip?ft.city.slice().sort(function(x,y){return y.length-x.length;})[0]||"":"";
    (indexes||[]).forEach(function(arr){
      (arr||[]).forEach(function(r){
        if(!r||skip.has(rowKey(r)))return;
        const hay=String(r.hay||"");
        if(hk&&hay.indexOf(hk)<0)return;
        if(zipOnly&&hay.indexOf(zipOnly)<0)return;
        if(cityWord&&hay.indexOf(cityWord)<0)return;
        const s=closeMatchScore(r,ft);
        if(s!=null)add(r,s);
      });
    });
  }
  return out.sort(function(x,y){return y.s-x.s||x.i-y.i;}).slice(0,o.limit||20)
    .map(function(x){return Object.assign({},x.r,{close:true,closeScore:x.s});});
}
// What the database's close-match search (search_leads_fuzzy, see
// supabase/2026-10-01-fuzzy-lead-search.sql) is asked for, or null when
// there's nothing it could add: {kind:"address", q: street words, house,
// zip} with a house number (or a zip), else {kind:"name", q: name words, zip}.
export function fuzzyRemoteParams(cls){
  const ft=fuzzyTermsFor(cls);
  if(!ft)return null;
  if(ft.street&&(ft.street.house||ft.zip)){
    return{kind:"address",q:ft.street.words.join(" "),house:ft.street.house?houseKey(ft.street.house):"",zip:ft.zip};
  }
  if(ft.name)return{kind:"name",q:ft.name.join(" "),house:"",zip:ft.zip};
  return null;
}

// ── The Phone box ─────────────────────────────────────────────
// Digits typed so far, without a leading US country code.
export function phoneDigits(v){
  let d=String(v||"").replace(/\D/g,"");
  if(d.length>1&&d.charAt(0)==="1")d=d.slice(1);
  return d;
}
// Formatted as typed: "480" → "(480) 5" → "(480) 555-1234". Never ends on a
// separator, so backspace always deletes a digit. A leading 1 is the country
// code; more than 10 digits is left alone (not a US number).
export function formatPhoneInput(v){
  const all=String(v||"").replace(/\D/g,"");
  if(!all)return"";
  const cc=all.charAt(0)==="1";
  const d=cc?all.slice(1):all;
  if(d.length>10)return all;
  if(cc&&!d.length)return"1";
  let f;
  if(d.length<=3)f=d;
  else if(d.length<=6)f="("+d.slice(0,3)+") "+d.slice(3);
  else f="("+d.slice(0,3)+") "+d.slice(3,6)+"-"+d.slice(6);
  return (cc?"+1 ":"")+f;
}
// Letters typed into the Phone box mean he's typing a name or an address
// there by habit. Returns what should move to the Name/address box ("4521 M"
// for "(452) 1 M"), or null when there are no letters.
export function lettersTypedInPhone(v){
  const s=String(v||"");
  const i=s.search(HAS_LETTER);
  if(i<0)return null;
  const head=s.slice(0,i).replace(/\D/g,"");
  return collapse((head?head+" ":"")+s.slice(i));
}
// The same keystrokes split the other way, for when the Phone box already
// holds a number worth keeping: {digits: what stays in Phone, letters: what
// moves down to Name/address}, or null when there are no letters. Nothing
// typed is ever dropped.
export function splitPhoneTyping(v){
  const s=String(v||"");
  const i=s.search(HAS_LETTER);
  if(i<0)return null;
  return{digits:s.slice(0,i).replace(/\D/g,""),letters:s.slice(i).replace(/^\s+/,"")};
}
// What the Phone box gives a brand-new lead when it does NOT hold a full US
// number (a full one is the caller's — see CallConsole.startNew):
// 7–9 digits are a local number or most of one, kept so it's on the card to
// finish ("555-1234"); more than 10 is a non-US number, kept as typed; fewer
// than 7 is a "last four" lookup, not a phone.
export function partialPhoneForLead(v){
  const s=collapse(v);
  if(s.replace(/\D/g,"").length>10)return s;
  const d=phoneDigits(s);
  if(d.length<7||d.length>=10)return "";
  return d.length===7?d.slice(0,3)+"-"+d.slice(3):s;
}
// A whole phone number typed or pasted into the Name/address box. Returns
// {phone: 10 digits, rest} or null. Needs 10 digits in phone shape with no
// digit touching either end, so house numbers and ZIP+4s never match.
export function extractPhone(text){
  const s=String(text||"");
  const m=/(^|[^\d])(\+?1[\s.-]?)?(\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})(?!\d)/.exec(s);
  if(!m)return null;
  const d=m[3].replace(/\D/g,"");
  if(d.length!==10)return null;
  const start=m.index+m[1].length;
  const rest=collapse((s.slice(0,start)+" "+s.slice(m.index+m[0].length)).replace(/^[\s,;-]+|[\s,;-]+$/g,""));
  return{phone:d,rest:rest};
}
